import React, { useEffect, useState } from 'react';

import { Controller } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import { useLocation, useNavigate } from 'react-router-dom';

import { Visibility, VisibilityOff } from '@mui/icons-material';
import { IconButton, InputAdornment, TextField } from '@mui/material';

import useRegister from './hooks/useRegister';
import * as S from './styles';
import Button from 'components/Button';
import Text from 'components/Text';
import WhatsappFloatingButton from 'components/WhatsappFloatingButton';
import { useToast } from 'hooks/useToast';
import GlobalModal from 'modals/GlobalModal/GlobalModal';
import { apiOrchestrator } from 'services/apiService';
import theme from 'styles/theme';
import { useIsDesktop, useIsMobile, useIsTablet } from 'utils/mediaQueries';
import { CompanyLogo } from 'utils/utilsIcons';

import 'react-phone-input-2/lib/style.css';

export const RegisterIframe: React.FC = () => {
  const [duplicateEmailError, setDuplicateEmailError] = useState(false);
  const { control, handleSubmit, errors, onSubmit, loading } = useRegister(setDuplicateEmailError);

  const navigate = useNavigate();
  const location = useLocation();

  const [inviteId, setInviteId] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteAction, setInviteAction] = useState<'accept' | 'decline' | null>(null);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showInviteRequiredModal, setShowInviteRequiredModal] = useState(false);

  const { toastSuccess, toastError } = useToast();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const invite = searchParams.get('invite_id');
    if (invite) {
      setInviteId(invite);
      setShowInviteModal(true);
    } else {
      setShowInviteRequiredModal(true);
    }
  }, [location.search]);

  const toggleShowPassword = () => setShowPassword(!showPassword);
  const toggleShowConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

  const handleInviteResponse = async (action: 'accept' | 'decline') => {
    setInviteAction(action);
    setShowInviteModal(false);

    if (action === 'decline' && inviteId) {
      try {
        await respondToInvite(inviteId, null, 'decline');
        toastSuccess('Convite recusado com sucesso.');
      } catch (error) {
        console.error('Erro ao recusar o convite:', error);
        toastError('Erro ao recusar o convite.');
      }
    }
  };

  const handleFormSubmit = async (data: any) => {
    try {
      const response = await onSubmit(data);

      if (!response) {
        return;
      }

      const userId = response.uuid;
      toastSuccess('Cadastro realizado com sucesso!');

      if (inviteId) {
        try {
          const inviteResponse = await respondToInvite(inviteId, userId, 'accept');

          if (inviteResponse && inviteAction === 'accept' && inviteResponse.invite) {
            const inviteeFullName = `${inviteResponse.invite.invitee_first_name} ${inviteResponse.invite.invitee_last_name}`;
            localStorage.setItem('inviteeName', inviteeFullName);
          }
        } catch (error) {
          console.error('Erro ao aceitar o convite:', error);
          toastError('Erro ao aceitar o convite.');
        }
      }

      localStorage.setItem('isSuccessFinal', 'true');
      navigate('/login');
    } catch (error: any) {
      if (
        error.response?.data?.detail?.includes('duplicate key value violates unique constraint')
      ) {
        setDuplicateEmailError(true);
      } else {
        let errorMessage = 'Erro ao realizar o cadastro. Verifique os dados e tente novamente.';
        if (error.response && error.response.data && error.response.data.detail) {
          errorMessage = error.response.data.detail;
        }
      }
    }
  };

  const respondToInvite = async (
    inviteId: string,
    userId: string | null,
    action: 'accept' | 'decline'
  ) => {
    const url = `/invites/invite/${inviteId}/respond-invite?action=${action}${userId ? `&user_id=${userId}` : ''}`;
    const response = await apiOrchestrator.post(url);
    return response.data;
  };

  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isDesktop = useIsDesktop();

  return (
    <S.Background>
      <S.Container>
        <S.Content>
          <S.Card>
            <S.Image src={CompanyLogo} />
          </S.Card>
          <S.Form onSubmit={handleSubmit(handleFormSubmit)}>
            <div className="padding-top-24"></div>
            <Text size={isMobile ? 'medium' : isTablet ? 'large' : 'xLarge'} className="text-title">
              Registre-se!
            </Text>

            <S.ContentForm>
              <S.NameSurnameContainer>
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Nome"
                      variant="outlined"
                      fullWidth
                      error={!!errors.firstName}
                      helperText={errors.firstName?.message}
                      size={isMobile ? 'small' : 'medium'}
                    />
                  )}
                />

                <Controller
                  name="lastName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Sobrenome"
                      variant="outlined"
                      fullWidth
                      error={!!errors.lastName}
                      helperText={errors.lastName?.message}
                      size={isMobile ? 'small' : 'medium'}
                    />
                  )}
                />
              </S.NameSurnameContainer>

              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="E-mail"
                    variant="outlined"
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    size={isMobile ? 'small' : 'medium'}
                  />
                )}
              />

              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <PhoneInput
                    {...field}
                    country={'br'}
                    specialLabel=""
                    placeholder="Telefone"
                    containerStyle={{
                      width: '100%',
                    }}
                    buttonStyle={{
                      backgroundColor: theme.COLORS.BACKGROUND_SECONDARY,
                      border: `1px solid ${errors.phone ? theme.COLORS.RED_1 : theme.COLORS.GRAY_40}`,
                    }}
                    dropdownStyle={{
                      backgroundColor: theme.COLORS.BACKGROUND_SECONDARY,
                    }}
                    inputStyle={{
                      width: '100%',
                      height: '56px',
                      backgroundColor: theme.COLORS.BACKGROUND_SECONDARY,
                      border: `1px solid ${errors.phone ? theme.COLORS.RED_1 : theme.COLORS.GRAY_40}`,
                      color: theme.COLORS.WHITE,
                    }}
                  />
                )}
              />
              {errors.phone && (
                <span
                  style={{
                    color: `${theme.COLORS.RED_1}`,
                    fontSize: '10px',
                    marginLeft: '1rem',
                    marginTop: '-0.5rem',
                  }}
                >
                  {errors.phone.message}
                </span>
              )}

              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Senha"
                    type={showPassword ? 'text' : 'password'}
                    variant="outlined"
                    fullWidth
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    size={isMobile ? 'small' : 'medium'}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={toggleShowPassword}
                            edge="end"
                          >
                            {showPassword ? (
                              <VisibilityOff
                                style={{
                                  color: errors.password ? theme.COLORS.RED_1 : 'inherit',
                                }}
                              />
                            ) : (
                              <Visibility
                                style={{
                                  color: errors.password ? theme.COLORS.RED_1 : 'inherit',
                                }}
                              />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              <Controller
                name="confirmPassword"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Confirme a Senha"
                    type={showConfirmPassword ? 'text' : 'password'}
                    variant="outlined"
                    fullWidth
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword?.message}
                    size={isMobile ? 'small' : 'medium'}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle confirm password visibility"
                            onClick={toggleShowConfirmPassword}
                            edge="end"
                          >
                            {showConfirmPassword ? (
                              <VisibilityOff
                                style={{
                                  color: errors.confirmPassword ? theme.COLORS.RED_1 : 'inherit',
                                }}
                              />
                            ) : (
                              <Visibility
                                style={{
                                  color: errors.confirmPassword ? theme.COLORS.RED_1 : 'inherit',
                                }}
                              />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </S.ContentForm>

            <S.ButtonsContainer>
              <Button
                type="submit"
                variant="minimal"
                color="primary"
                size="medium"
                onClick={() => navigate('/login')}
                fullWidth
              >
                Voltar
              </Button>

              <Button
                type="submit"
                variant="primary"
                color="primary"
                fullWidth
                disabled={loading || !inviteId}
              >
                {loading ? 'Carregando...' : 'Registrar-se'}
              </Button>
            </S.ButtonsContainer>
          </S.Form>
        </S.Content>

        <GlobalModal
          open={duplicateEmailError}
          title="ERRO DE CADASTRO"
          description="O e-mail informado já está cadastrado. Tente usar outro e-mail ou recupere sua senha."
          onClose={() => setDuplicateEmailError(false)}
          confirmButtonText="Fechar"
        />

        <GlobalModal
          open={showInviteModal}
          title="CONVITE DE REGISTRO"
          description="Você recebeu um convite para se registrar. Deseja aceitá-lo?"
          onClose={() => setShowInviteModal(false)}
          onConfirm={() => handleInviteResponse('accept')}
          confirmButtonText="Aceitar"
          cancelButtonText="Recusar"
          onCancel={() => handleInviteResponse('decline')}
        />

        <GlobalModal
          open={showInviteRequiredModal}
          title="CONVITE NECESSÁRIO"
          description="Para registrar-se, é necessário um link de convite de um usuário ativo da comunidade."
          onClose={() => navigate('/login')}
          confirmButtonText="Fechar"
        />
      </S.Container>
      <WhatsappFloatingButton />
    </S.Background>
  );
};
